import { Component, OnInit, OnDestroy, Input, ViewChild } from '@angular/core';

import { BehaviorSubject, Observable, Subscription, map, of, switchMap } from 'rxjs';

import { TagModel } from '../../models/tag.model';
import { TagsService } from '../../services/tags.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ManageTagsDialogComponent } from '../ManageTagsDialog/manage-tags-dialog.component';
import { AppearCondition, AutocompleteCustomOption } from '../../../Forms/components/Autocomplete/autocomplete-custom-option.model';
import { AutocompleteComponent } from '../../../Forms/components/Autocomplete/autocomplete.component';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ChipModel } from '../../../Forms/models/chip.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { ControlValueAccessorModel, getProvider } from '../../../Forms/models/control-value-accessor.model';

@Component({
    selector: 'agpr-tags-picker',
    templateUrl: './tags-picker.component.html',
    styleUrls: ['./tags-picker.component.scss'],
    providers: [
        getProvider(TagsPickerComponent)
    ],
    standalone: false
})
/**
 * Parent component to this needs to handle adding and removing tags to its own model.
 * As well as providing as input to this component the array ids for the currently applied tags.
 *
 * This component will emit events indicating the array of requested tagIds to be applied to the parent.
 */
export class TagsPickerComponent extends ControlValueAccessorModel<number[]> implements OnInit, OnDestroy {
    /**
     * Inputs the tagIds currently applied to the parent model
     */
    @Input() id: string = null;
    @Input() value: number[] = [];
    @Input() showHeader: boolean = true;
    @Input() showManageTags: boolean = true;
    @Input() maxNumberOfTags: number;
    @Input() errorMessage: string;
    @Input() placeholder: string = 'Tags.inputPlaceholder';
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.social;
    @Input() isInTable: boolean = false;

    @ViewChild('autocomplete') autocomplete: AutocompleteComponent;

    public _onChange: Function = this.defaultOnChange;
    public _onTouched: Function;

    tagChips: ChipModel[] = [];
    selectedTag: TagModel;
    availableTags: TagModel[] = [];
    filteredTags: TagModel[] = [];
    createTagsModel: TagModel =  new TagModel();
    maxTagsSelected: boolean;

    readonly buttonTypes = ButtonTypesEnum;
    readonly agprComponentStyleEnum = AgprComponentStyleEnum;

    subscriptions: Subscription[] = [];
    tagList$: Observable<TagModel[]>;

    tagsAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Tags.createTag',
            action: (searchString: string) => {
                const tagModel = new TagModel();
                tagModel.name = searchString;

                this.tagsService.createTagWithoutFetch(tagModel).pipe(
                    switchMap((id) => this.tagsService.refreshAllManual().pipe(
                        switchMap(() => {
                            this.addToValue(id);
                            this.autocomplete.clearInput();
                            return of(null);
                        }),
                    ))
                ).subscribe();
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    constructor(
        public tagsService: TagsService,
        public coreDialogService: CoreDialogService
    ) {
        super();
    }

    ngOnInit() {
        this.tagList$ = this.tagsService.all$;

        this.subscriptions.push(
            this.tagList$.subscribe(
                tagsList => {
                    if (!tagsList) {
                        return;
                    }

                    this.availableTags = tagsList.slice();
                    this.filteredTags = this.availableTags;
                    this.sortFilteredTags();
                    this.populateChips();
                    this.filterOutRemovedTags();
                }
            )
        );
    }




    // TODO : remove
    autocompleteTags(query: string): void {
        query = query.trim();
        const queryString = query.toLowerCase();
        let queryIsExactMatch: boolean = false;

        this.filteredTags = this.availableTags.filter((tag) => {
            const tagName = tag.name.toLowerCase();

            if (tagName === queryString) {
                queryIsExactMatch = true;
            }

            if (this.value.includes(tag.id)) {
                return false;
            }

            return tagName.indexOf(queryString) >= 0;
        });

        this.sortFilteredTags();
        if (query.trim() !== '' && !queryIsExactMatch) {
            this.createTagsModel = new TagModel();
            this.createTagsModel.id = -1;
            this.createTagsModel.name = query;
            this.filteredTags.unshift(this.createTagsModel);
        }
    }

    /**
     * Fetches chip models for selected tagIds
     */
    private populateChips() {
        this.tagsService.getTagsByIds(this.value).pipe(
            map(tags => tags.map(tag => tag?.toChipModel()))
        ).subscribe((tags: ChipModel[]) => {
            this.tagChips = tags.filter(chip => !!chip);
            this.filterOutRemovedTags();
        });
    }

    /**
     * There is a chance sometimes where you can have some tags on a mention that do not exist in the account...
     * and since we use the index to remove tags from a mention, we need to filter out all tags that do not exist in the account
     */
    filterOutRemovedTags() {
        if (this.tagChips?.length && this.filteredTags?.length) {
            const allTagIDs = this.filteredTags.map(tag => tag.id);
            this.value = this.value.filter((tagId) => allTagIDs.includes(tagId));
        }
    }

    // TODO : remove
    sortFilteredTags(): void {
        this.filteredTags.sort((a, b) => {
            const nameOfA = a.name.toLowerCase();
            const nameOfB = b.name.toLowerCase();

            return nameOfA.localeCompare(nameOfB);
        });
    }

    /**
     * Handles selection of a dropdown item
     */
    selectTag(tag: TagModel): void {
        if (!this.value.includes(tag.id)) {
            this.addToValue(tag.id);
        }
        this.autocomplete.clearInput();
    }

    /**
     * Adds to value array
     */
    addToValue(tagId: number): void {
        // set maxTagsSelected only if user tries to go over max
        this.setMaxTagsSelected();

        if (this.maxTagsSelected) {
            return;
        }
        this.value = [...this.value, tagId];
        this._onChange(this.value);
        this.populateChips();
    }

    /**
     * Setter for maxTagSelected
     */
    setMaxTagsSelected() {
        if (!this.maxNumberOfTags) {
            return;
        }
        this.maxTagsSelected = this.value.length === this.maxNumberOfTags;
    }

    manageButtonClicked(): void {


        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        if (this.autocomplete?.autocomplete) {
            this.autocomplete.autocomplete.hide();
        }
        this.coreDialogService.open(ManageTagsDialogComponent, io);

    }

    /**
     * Emits event to update the currently selected tags
     */
    tagRemoved(index): void {
        this.value = this.tagChips.map(tagChip => tagChip.id);
        this.value.splice(index, 1);
        this._onChange(this.value);
        this.setMaxTagsSelected();
        this.populateChips();
    }

    defaultOnChange() {}

    writeValue(tagIds: number[]) {
        if (!tagIds) {
            return;
        }

        this.value = tagIds;
        this.populateChips();
    }

    registerOnChange(onChange: Function) {
        this._onChange = onChange;
    }

    registerOnTouched(onTouched: Function) {
        this._onTouched = onTouched;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
